import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/services/api';
import { LoginRequest } from '@/types';
import { toast } from 'sonner';

export const useAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { login, logout, user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const handleLogin = async (data: LoginRequest) => {
    setIsLoading(true);
    try {
      const response = await authApi.login(data);
      
      // Si la respuesta requiere 2FA, lanzar error para que lo maneje el componente
      if (response.data.requires2FA) {
        const error = new Error(response.data.message || 'Código de verificación requerido');
        (error as any).tempToken = response.data.tempToken;
        (error as any).requires2FA = true;
        throw error;
      }
      
      const { user, token } = response.data;
      login(user, token);
      toast.success('¡Bienvenido de vuelta!');
      navigate('/');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error al iniciar sesión';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginWith2FA = async (tempToken: string, twoFactorCode: string) => {
    setIsLoading(true);
    try {
      const response = await authApi.loginWith2FA({ tempToken, twoFactorCode });
      const { user, token } = response.data;
      login(user, token);
      toast.success('¡Bienvenido de vuelta!');
      navigate('/');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error al verificar código';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
      logout();
      toast.success('Sesión cerrada exitosamente');
      navigate('/login');
    } catch (error) {
      // Even if the API call fails, we still logout locally
      logout();
      navigate('/login');
    }
  };

  const checkPermissions = (requiredRoles: string[]) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    login: handleLogin,
    loginWith2FA: handleLoginWith2FA,
    logout: handleLogout,
    checkPermissions,
  };
};